import { excludeNull, insertBetween } from "@/shared/lib/collection";
import { BaseButton } from "@/shared/ui/BaseButton";
import { BaseIcon } from "@/shared/ui/BaseIcons";
import { BaseDivider } from "@/shared/ui/base-divider";
import { Stack, Typography } from "@mui/material";
import type React from "react";
import type { ActionButtonProps } from "./action-buttons";
import { StatusTitle, type StatusTitleProps } from "./status-title";

export type OfferCargoStatusContentProps = {
  statusTitle: StatusTitleProps;
  texts: (
    | {
        text: string;
        color?: string;
      }
    | undefined
  )[];
  alerts: (
    | {
        title: string;
        subTitle?: string;
        text?: string;
        variant: "warning";
      }
    | undefined
  )[];
  actionButtons?: (ActionButtonProps | undefined)[];
};

export const OfferCargoStatusContent: React.FC<
  OfferCargoStatusContentProps
> = ({ statusTitle, texts, alerts, actionButtons = [] }) => {
  return (
    <Stack direction="column" spacing={2} flex={{ xs: 1, sm: "auto" }}>
      <Stack direction="column" spacing={1}>
        <StatusTitle {...statusTitle} />
        {excludeNull(texts).length > 0 && (
          <Stack direction="column" spacing={0}>
            {excludeNull(texts).map((text) => (
              <Typography key={text.text} variant="body2" color={text.color}>
                {text.text}
              </Typography>
            ))}
          </Stack>
        )}
        {excludeNull(alerts).length > 0 && (
          <Stack direction="column" spacing={1}>
            {excludeNull(alerts).map((alert) => (
              <Stack
                key={alert.title}
                px={2}
                py={1.5}
                spacing={1}
                borderRadius="16px"
                bgcolor={"#FEF0C7"}
              >
                <Stack spacing={1} direction="row" justifyItems="center">
                  <BaseIcon
                    name={"messageBalloon"}
                    htmlColor={"#DC6803"}
                    sx={{ fontSize: "20px" }}
                  />
                  <Typography
                    variant="body2"
                    color={"#DC6803"}
                    fontWeight={700}
                  >
                    {alert.title}
                  </Typography>
                </Stack>
                {alert.subTitle && (
                  <Typography
                    variant="body2"
                    color={"#DC6803"}
                    fontWeight={700}
                  >
                    {alert.subTitle}
                  </Typography>
                )}
                {alert.text && (
                  <Typography variant="body2" color={"#DC6803"}>
                    {alert.text}
                  </Typography>
                )}
              </Stack>
            ))}
          </Stack>
        )}
      </Stack>
      {excludeNull(actionButtons).length > 0 && (
        <Stack direction="column" spacing={1}>
          {excludeNull(actionButtons).map((actionButton) => (
            <BaseButton key={actionButton.label} {...actionButton}>
              {actionButton.label}
            </BaseButton>
          ))}
        </Stack>
      )}
    </Stack>
  );
};

export type OfferCargoStatusContentsProps = {
  statusContents: (OfferCargoStatusContentProps | undefined)[];
};

export const OfferCargoStatusContents: React.FC<
  OfferCargoStatusContentsProps
> = ({ statusContents }) => {
  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={{ xs: 2, sm: 3 }}
      px={3}
      py={2}
      alignItems="stretch"
    >
      {insertBetween(
        excludeNull(statusContents).map((statusContent) => {
          return (
            <OfferCargoStatusContent
              key={JSON.stringify(statusContent)}
              {...statusContent}
            />
          );
        }),
        <BaseDivider orientation="vertical" />,
      )}
    </Stack>
  );
};
